import LexicalEditor, { type LexicalEditorHandle } from '@/components/Editor/LexicalEditor'
import { type Attachment } from '@/states/attachment'
import { css } from '@emotion/react'
import { COMMAND_PRIORITY_NORMAL, PASTE_COMMAND } from 'lexical'
import { isEmpty } from 'lodash-es'
import {
	type ClipboardEvent,
	type CompositionEvent,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'
import { type ControllerRenderProps, useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { filterTransferItems } from '../Attachment/utils'
import type { PostFormValue } from './PostForm/schemas'

export interface ContentEditorProps
	extends Omit<ControllerRenderProps<PostFormValue, 'content'>, 'ref'> {
	/**
	 *
	 * @default false
	 */
	autoFocus?: boolean
}

export default forwardRef<{ editor: LexicalEditorHandle | null }, ContentEditorProps>(
	function ContentEditor(
		{ onChange, onBlur, value, disabled, autoFocus = false, ...props }: ContentEditorProps,
		ref
	) {
		const { setValue, getValues } = useFormContext<PostFormValue>()
		const editorHandleRef = useRef<LexicalEditorHandle>(null)
		const isCompositingRef = useRef<boolean>(false)

		const handleEditorValueUpdate = useCallback(
			(newValue: string) => {
				if (isCompositingRef.current) {
					return
				}
				onChange(newValue)
			},
			[onChange]
		)

		const handlePaste = (e: ClipboardEvent<HTMLElement>) => {
			const files = Array.from(e.clipboardData.files)
			const dataTransferItems = Array.from(e.clipboardData.items)

			if (isEmpty(files) || isEmpty(dataTransferItems)) {
				return false
			}
			e.preventDefault()

			const { images, videos } = filterTransferItems(dataTransferItems)

			// Validate that images and videos cannot be dropped simultaneously
			if (images.length > 0 && videos.length > 0) {
				// Show toast notification to inform user
				toast.error('不同同时添加图片和视频')
				return false
			}

			const attachments = getValues('attachments')
			setValue('attachments', [...attachments, ...images, ...videos] as Attachment[])

			return true
		}

		useImperativeHandle(
			ref,
			() => ({
				get editor() {
					return editorHandleRef.current
				},
			}),
			[]
		)

		useEffect(() => {
			const editor = editorHandleRef.current?.editor

			if (!editor) {
				return
			}
			editor.registerCommand(
				PASTE_COMMAND,
				(payload) => handlePaste(payload as unknown as ClipboardEvent<HTMLElement>),
				COMMAND_PRIORITY_NORMAL
			)
		}, [handlePaste])

		return (
			<LexicalEditor
				spellCheck={false}
				value={value}
				ref={editorHandleRef}
				placeholder="分享你的想法..."
				onChange={handleEditorValueUpdate}
				data-autofocus={autoFocus}
				onCompositionStart={() => {
					isCompositingRef.current = true
				}}
				onCompositionEnd={(e: CompositionEvent) => {
					isCompositingRef.current = false
					editorHandleRef.current?.getText().then(onChange)
				}}
				className="content-editor"
				css={css`
					font-size: 20px;
					font-weight: 400;
					line-height: 28px;
					color: var(--tint-primary);
				`}
			/>
		)
	}
)
